import React, { useEffect, useState } from "react";
import { axiosPost } from "../../../utils/AxiosApi";
import { URL } from "../../../utils/Constants";
import { useARRCode } from "../context/ARRLevelContext";
import swal from "sweetalert";

export const useEducationLevelByAssessmentModule = () => {
  const { arrLevel } = useARRCode();
  const [educationLevel, setEducationLevel] = useState([]);

  const getEducationLevel = () => {
    axiosPost(
      URL.getEducationLevelByAssessmentModule,
      {
        code: arrLevel,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data || [];
          const processedData = data?.map((item) => ({
            label: item?.name,
            value: item?.id,
          }));
          setEducationLevel(processedData);
        }
      },
    );
  };

  useEffect(() => {
    getEducationLevel();
  }, [arrLevel]);

  return { educationLevel };
};

export const useEducationLevelDivisions = () => {
  const [educationDivision, setEducationDivision] = useState([]);

  const getEducationLevelDivision = (id) => {
    axiosPost(
      URL.getEducationLevelDivisions,
      {
        educationLevelId: id,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          const processedData = data?.map((item) => ({
            label: item.name,
            value: item.id,
          }));
          setEducationDivision(processedData);
        }
      },
    );
  };

  return { educationDivision, getEducationLevelDivision };
};

export const useAssessmentType = () => {
  const { arrLevel } = useARRCode();
  const [assessmentType, setAssessmentType] = useState([]);

  const getAssessmentType = () => {
    axiosPost(
      URL.getAssessmentTypes,
      {
        code: arrLevel,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          const processedData = data?.map((item) => ({
            label: item.name,
            value: item.code,
          }));
          setAssessmentType(processedData);
        }
      },
    );
  };

  useEffect(() => {
    getAssessmentType();
  }, [arrLevel]);

  return { assessmentType };
};

export const useYearLevelByEducationLevel = () => {
  const { arrLevel } = useARRCode();
  const [yearLevel, setYearLevel] = useState([]);

  const getYearLevelByEducationLevel = (
    educationLevelId,
    educationLevelDivisionId,
  ) => {
    axiosPost(
      URL.getYearLevelByEducationLevel,
      {
        assessmentModuleCode: arrLevel,
        educationLevelDivisionId: educationLevelDivisionId,
        educationLevelId: educationLevelId,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setYearLevel(data);
        }
      },
    );
  };

  return { yearLevel, setYearLevel, getYearLevelByEducationLevel };
};

export const useAssessment = (pageSize) => {
  const { arrLevel } = useARRCode();
  const [assessments, setAssessments] = useState([]);
  const [totalRecordsCount, setTotalRecordsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const getAssessments = (page) => {
    setLoading(true);
    let offset = page * pageSize - pageSize;
    axiosPost(
      URL.selectAssessments,
      {
        assessmentModuleCode: arrLevel,
        offset: offset || 0,
        rowCountLimit: pageSize || null,
      },
      (response) => {
        if (response.status === 200) {
          setTotalRecordsCount(response?.data?.totalRecordsCount);
          const data = response?.data?.data;
          setAssessments(data);
        }
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    getAssessments();
  }, [arrLevel]);

  return {
    loading,
    assessments,
    totalRecordsCount,
    refetchAssessment: getAssessments,
  };
};

// export const useCopyPreviousAssessment = () => {
//   const [loading, setLoading] = useState(false);

//   const copyPreviousAssessment = (id) => {
//     if (!id) return;
//     setLoading(true);
//     axiosPost(
//       URL.getAssessmentConfiguration,
//       { id: id },
//       (response) => {
//         if (response.status === 200) {
//           const data = response?.data?.data;
//           setPreviousAssessment(data);
//         }
//         setLoading(false);
//       },
//       (error) => {
//         swal(
//           "Oops",
//           error?.response?.data?.message ||
//             "Failed to copy previous assessments",
//         );
//         setLoading(false);
//       },
//     );
//   };

//   return {
//     previousAssessment,
//     setPreviousAssessment,
//     isLoadingPreviousAssessments: loading,
//     copyPreviousAssessment,
//   };
// };

export const useAssessmentConfiguration = () => {
  const { arrLevel } = useARRCode();
  const [loading, setLoading] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const [assessmentConfiguration, setAssessmentConfiguration] = useState(null);

  const getAssessmentConfiguration = (id) => {
    if (!id) return;
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.getAssessmentConfiguration,
      { id: id },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setAssessmentConfiguration(data);
        }
        setLoading((prev) => ({ ...prev, read: false }));
      },
      (error) => {
        swal(
          "Oops",
          error?.response?.data?.message ||
            "Failed to copy previous assessments",
        );
        setLoading((prev) => ({ ...prev, read: false }));
      },
    );
  };

  const createAssessmentConfiguration = (data) => {
    setLoading((prev) => ({ ...prev, create: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.insertAssessment,
        data,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment Created Successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, create: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, create: false }));
          swal(
            "Oops",
            error?.response?.data?.message || "Failed creating assessment",
          );
          reject(error);
        },
      );
    });
  };

  const updateAssessmentConfiguration = (data) => {
    setLoading((prev) => ({ ...prev, update: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateAssessment,
        data,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment Updated Successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, update: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, update: false }));
          swal(
            "Oops",
            error?.response?.data?.message || "Failed updating assessment",
          );
          reject(error);
        },
      );
    });
  };

  const deleteAssessment = (id) => {
    setLoading((prev) => ({ ...prev, delete: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.cancelAssessment,
        { id: id },
        (response) => {
          if (response.status === 200) {
            swal("Success", "Assessment deleted Successfully");
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, delete: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, delete: false }));
          swal(
            "Oops",
            error?.response?.data?.message || "Failed to delete assessment",
          );
          reject(error);
        },
      );
    });
  };

  useEffect(() => {
    setAssessmentConfiguration(null);
  }, [arrLevel]);

  return {
    assessmentConfiguration,
    loading,
    setAssessmentConfiguration,
    getAssessmentConfiguration,
    createAssessmentConfiguration,
    updateAssessmentConfiguration,
    deleteAssessment,
  };
};

export const useGeneralAndSubjectConfig = () => {
  const [generalAndSubjectConfig, setGeneralAndSubjectConfig] = useState(null);

  const getGeneralAndSubjectConfig = (id) => {
    if (!id) {
      swal("Oops", "Assessment id is missing");
    }
    axiosPost(URL.getAssessmentConfiguration, { id: id }, (response) => {
      if (response.status === 200) {
        setGeneralAndSubjectConfig(response?.data?.data || []);
      }
    });
  };

  return { generalAndSubjectConfig, getGeneralAndSubjectConfig };
};

export const useYearLevelByAssessment = () => {
  const [yearLevelByAssessment, setYearLevelByAssessment] = useState([]);
  const [loading, setLoading] = useState(false);

  const getYearLevelByAssessment = (id) => {
    if (!id) {
      swal("Oops", "Assessment id is missing");
    }
    setLoading(true);
    axiosPost(
      URL.getYearLevelByAssessment,
      { assessmentId: id },
      (response) => {
        if (response.status === 200) {
          setYearLevelByAssessment(response?.data?.data || []);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
      },
    );
  };

  return {
    loading,
    yearLevelByAssessment,
    setYearLevelByAssessment,
    getYearLevelByAssessment,
  };
};

export const useSelectConfigForAssessment = () => {
  const [aggregationConfig, setAggregationConfig] = useState(null);

  const selectConfigForAssessment = (id) => {
    if (!id) {
      swal("Oops", "Assessment id is missing");
    }
    axiosPost(
      URL.getAssessmentForUpdate,
      { targetAssessment: id },
      (response) => {
        if (response.status === 200) {
          setAggregationConfig(response?.data?.data || []);
        }
      },
    );
  };

  return { aggregationConfig, selectConfigForAssessment };
};

export const useSubjectListForSubjectAssessmentSetting = (payload) => {
  const [subjectConfig, setSubjectConfig] = useState([]);
  const [loading, setLoading] = useState(false);

  const getSubjectConfig = () => {
    setLoading(true);
    axiosPost(
      URL.getSubjectListForSubjectAssessmentSetting,
      payload,
      (response) => {
        if (response.status === 200) {
          setSubjectConfig(response?.data?.data);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    getSubjectConfig();
  }, [payload]);

  return { subjectConfig, loading };
};

export const useGeneralAssessmentSetting = (payload) => {
  const [generalConfig, setGeneralConfig] = useState([]);
  const [loading, setLoading] = useState(false);

  const getGeneralConfig = () => {
    setLoading(true);
    axiosPost(
      URL.generalAssessmentSelect,
      {
        assessmentId: payload.assessmentId,
        classroomId: payload.classroomId,
        yearLevelId: payload.yearLevelId,
      },
      (response) => {
        if (response.status === 200) {
          setGeneralConfig(response?.data?.data);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    getGeneralConfig();
  }, [payload]);

  return { generalConfig, loading };
};

export const useAssessmentSubjectScaleConfigGroupsForSubjectAssessmentConfig = (
  payload,
) => {
  const [subjectAssessmentConfig, setSubjectAssessmentConfig] = useState([]);
  const [loading, setLoading] = useState({
    read: false,
    update: false,
  });

  const getsubjectAssessmentConfig = () => {
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.selectAssessmentSubjectScaleConfigGroupsForSubjectAssessmentConfig,
      payload,
      (response) => {
        if (response.status === 200) {
          setSubjectAssessmentConfig(response?.data?.data);
        }
        setLoading((prev) => ({ ...prev, read: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, read: false }));
      },
    );
  };

  const updateSubjectConfiguraiton = (params) => {
    setLoading((prev) => ({ ...prev, update: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateSubjectConfiguration,
        params,
        (response) => {
          if (response.status === 200) {
            swal(
              "Success",
              response?.data?.message || "Subject Configured Successfully",
            );
            getsubjectAssessmentConfig();
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, update: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, update: false }));
          swal(
            "Oops",
            error?.response?.data?.message || "Failed to configure subject",
          );
          reject(error);
        },
      );
    });
  };

  useEffect(() => {
    getsubjectAssessmentConfig();
  }, [payload]);

  return { subjectAssessmentConfig, loading, updateSubjectConfiguraiton };
};

export const useClassroomForSubjectAssessmentSetting = (payload) => {
  const [
    classroomForSubjectAssessmentSetting,
    setClassroomForSubjectAssessmentSetting,
  ] = useState([]);

  const params = {
    assessmentId: payload.assessmentId,
    yearLevelId: payload.yearLevelId,
  };

  const getClassroomForSubjectAssessmentSetting = () => {
    axiosPost(
      URL.selectClassroomForSubjectAssessmentSetting,
      params,
      (response) => {
        if (response.status === 200) {
          setClassroomForSubjectAssessmentSetting(response?.data?.data);
        }
      },
    );
  };

  useEffect(() => {
    getClassroomForSubjectAssessmentSetting();
  }, [payload]);

  return {
    classroomForSubjectAssessmentSetting,
    getClassroomForSubjectAssessmentSetting,
  };
};

export const useImportSubjectConfiguration = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const importSubjectConfiguration = (params) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.importSubjectConfiguration,
        params,
        (response) => {
          if (response.status === 200) {
            setData(response?.data?.data);
            resolve(response);
          } else {
            reject(response);
          }
          setLoading(false);
        },
        (error) => {
          setLoading(false);
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to import subject configuration",
          );
          reject(error);
        },
      );
    });
  };

  return {
    data,
    loading,
    importSubjectConfiguration,
  };
};

export const useImportGeneralConfiguration = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const importGeneralConfiguration = (params) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.importGeneralConfiguration,
        params,
        (response) => {
          if (response.status === 200) {
            setData(response?.data?.data);
            resolve(response);
          } else {
            reject(response);
          }
          setLoading(false);
        },
        (error) => {
          setLoading(false);
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to import general configuration",
          );
          reject(error);
        },
      );
    });
  };

  return {
    data,
    loading,
    importGeneralConfiguration,
  };
};

export const useClassroomForGeneralAssessmentSetting = (payload) => {
  const [
    classroomForGeneralAssessmentSetting,
    setClassroomForGeneralAssessmentSetting,
  ] = useState([]);

  const params = {
    assessmentId: payload.assessmentId,
    yearLevelId: payload.yearLevelId,
  };

  const getClassroomForGeneralAssessmentSetting = () => {
    axiosPost(URL.selectClassroomList, params, (response) => {
      if (response.status === 200) {
        setClassroomForGeneralAssessmentSetting(response?.data?.data);
      }
    });
  };

  useEffect(() => {
    getClassroomForGeneralAssessmentSetting();
  }, [payload]);

  return {
    classroomForGeneralAssessmentSetting,
    getClassroomForGeneralAssessmentSetting,
  };
};

export const useGeneralAssessmentAccessorRole = () => {
  const [assessorRole, setAssessorRole] = useState([]);

  const generalAssessmentAccessorRole = () => {
    axiosPost(URL.selectGeneralAssessmentAccessorRole, {}, (response) => {
      if (response.status === 200) {
        setAssessorRole(response?.data?.data);
      }
    });
  };

  useEffect(() => {
    generalAssessmentAccessorRole();
  }, []);

  return {
    assessorRole,
  };
};

export const useGeneralConfiguration = (payload) => {
  const [generalConfiguration, setGeneralConfiguration] = useState([]);
  const [loading, setLoading] = useState({
    insert: false,
    read: false,
  });

  const getGeneralConfiguration = () => {
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.getGeneralAssessment,
      payload,
      (response) => {
        if (response.status === 200) {
          setGeneralConfiguration(response?.data?.data);
        }
        setLoading((prev) => ({ ...prev, read: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, read: false }));
      },
    );
  };

  const saveGeneralConfiguration = (params) => {
    setLoading((prev) => ({ ...prev, insert: true }));
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateGeneralConfiguration,
        params,
        (response) => {
          if (response.status === 200) {
            swal("Success", "Successfully updated general configuration");
            getGeneralConfiguration(payload);
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, insert: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, insert: false }));
          swal(
            "Oops",
            error?.response?.data?.message ||
              "Failed to update general configuration",
          );
          reject(error);
        },
      );
    });
  };

  useEffect(() => {
    getGeneralConfiguration();
  }, []);

  return {
    loading,
    generalConfiguration,
    saveGeneralConfiguration,
  };
};

//apply setting to
export const useSubjectConfigApplySetttings = () => {
  const [loading, setLoading] = useState(false);

  const applySettingsHandler = (params) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.subjectConfiguartionApplySettingTo,
        params,
        (response) => {
          if (response.status === 200) {
            swal(
              "Success",
              response?.data?.data || "Successfully applied settings",
            );
            resolve(response);
          } else {
            reject(response);
          }
          setLoading(false);
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message || "Failed to apply settings",
          );
          reject(error);
        },
      );
    });
  };

  return {
    loading,
    applySettingsHandler,
  };
};

export const useGeneralConfigApplySetttings = () => {
  const [loading, setLoading] = useState(false);

  const applySettingsHandler = (params) => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.generalConfiguartionApplySettingTo,
        params,
        (response) => {
          if (response.status === 200) {
            swal(
              "Success",
              response?.data?.data || "Successfully applied settings",
            );
            resolve(response);
          } else {
            reject(response);
          }
          setLoading(false);
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message || "Failed to apply settings",
          );
          reject(error);
        },
      );
    });
  };

  return {
    loading,
    applySettingsHandler,
  };
};
